import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Target, Crown, Coins, Users, Clock, Zap } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Page, PageTitle, Loader, Empty } from "../components/UI";
import { useEventStream } from "../lib/sse";

const TIER_TINT = {
  BRONZE: "text-amber-500", SILVER: "text-slate-300", GOLD: "text-[#E5B842]",
};

export default function Strike5Page() {
  const { player, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/strike5").then(({ data }) => setData(data)).catch(() => setData({ open: [], running: [], completed: [] }));
    if (player) api.get("/strike5/mine").then(({ data }) => setMine(data.tournaments)).catch(() => setMine([]));
  }, [player]);

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEventStream("/live/stream", (ev) => { if (ev?.type?.startsWith?.("strike5.")) load(); });

  const enter = async (t) => {
    setBusy(t.id);
    try {
      await api.post(`/strike5/${t.id}/enter`);
      toast.success(`Entered ${t.public_id} — ${t.buy_in} VP`);
      await refresh();
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && d.message) || apiErr(e));
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <Loader label="Loading Strike 5…" />;

  return (
    <Page>
      <PageTitle eyebrow="Individual Discipline · Virtual Points"
        title="Strike 5"
        subtitle="Solo knockout. 5-player pentads, one qualifier per pentad per round, last player standing takes the pot. Entry is a Virtual Points buy-in — VP has no monetary value." />

      {player && (
        <div data-testid="strike5-mine" className="mb-8">
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3">My Tournaments</h2>
          {mine.length === 0 ? (
            <Empty label="You have not entered any tournaments yet." />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {mine.map((t) => <TournamentCard key={t.id} t={t} mine />)}
            </div>
          )}
        </div>
      )}

      <section data-testid="strike5-open" className="mb-8">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3">
          <Zap className="w-5 h-5 text-[#E5B842]" /> Open for Entry
        </h2>
        {data.open.length === 0 ? <Empty label="No open tournaments right now — new ones open continuously." /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.open.map((t) => (
              <TournamentCard key={t.id} t={t}
                action={player ? (
                  <button data-testid={`enter-${t.id}`} disabled={busy === t.id}
                    onClick={() => enter(t)}
                    className="w-full mt-3 px-4 py-2 rounded-lg font-heading font-bold uppercase text-sm bg-[#E5B842] text-[#000000] hover:bg-[#f0c85a] disabled:opacity-50"
                    style={{ transition: "background-color 0.15s ease" }}>
                    {busy === t.id ? "Entering…" : `Enter · ${t.buy_in} VP`}
                  </button>
                ) : (
                  <Link to="/login" className="block w-full mt-3 px-4 py-2 rounded-lg font-heading font-bold uppercase text-sm text-center bg-[#0f0f0f] border border-[#2b2512] text-slate-300">
                    Log in to enter
                  </Link>
                )} />
            ))}
          </div>
        )}
      </section>

      <section data-testid="strike5-running" className="mb-8">
        <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3">In Progress</h2>
        {data.running.length === 0 ? <Empty label="No tournaments in progress." /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.running.map((t) => <TournamentCard key={t.id} t={t} />)}
          </div>
        )}
      </section>

      <section data-testid="strike5-completed">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3">
          <Crown className="w-5 h-5 text-[#E5B842]" /> Recent Champions
        </h2>
        {data.completed.length === 0 ? <Empty label="No completed tournaments yet." /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.completed.map((t) => <TournamentCard key={t.id} t={t} />)}
          </div>
        )}
      </section>
    </Page>
  );
}

function TournamentCard({ t, action, mine }) {
  return (
    <div data-testid={`tournament-${t.id}`}
      className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4 hover:border-[#E5B842]/40"
      style={{ transition: "border-color 0.15s ease" }}>
      <Link to={`/strike5/${t.id}`} className="block">
        <div className="flex items-center justify-between">
          <span className={`font-heading font-black uppercase tracking-wide ${TIER_TINT[t.tier]}`}>
            <Target className="w-4 h-4 inline mr-1 -mt-1" />{t.tier}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{t.status.replace(/_/g, " ")}{t.status === "RUNNING" ? ` · R${t.round}` : ""}</span>
        </div>
        <div className="font-mono text-xs text-slate-400 mt-1">{t.public_id}</div>
        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
          <Mini icon={<Coins className="w-3 h-3" />} label="Buy-in" value={`${t.buy_in}`} />
          <Mini icon={<Users className="w-3 h-3" />} label="Field" value={`${t.entries_count}/${t.capacity}`} />
          <Mini icon={<Crown className="w-3 h-3" />} label="Pot" value={`${t.pot}`} />
        </div>
        {t.champion && (
          <div className="mt-3 pt-3 border-t border-[#2b2512] flex items-center gap-1.5 text-sm">
            <Crown className="w-4 h-4 text-[#E5B842]" />
            <span className="font-bold text-[#E5B842] truncate">{t.champion.display_name}</span>
          </div>
        )}
        {mine && t.status === "RUNNING" && (
          <div className="mt-2 text-[11px] font-mono text-emerald-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Live — open tournament</div>
        )}
      </Link>
      {action}
    </div>
  );
}

function Mini({ icon, label, value }) {
  return (
    <div className="rounded-lg bg-[#000000] border border-[#2b2512] py-2">
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-mono flex items-center justify-center gap-0.5">{icon}{label}</div>
      <div className="font-heading font-black text-slate-100 tabular-nums text-sm">{value}</div>
    </div>
  );
}
